/** @format */

import React, { useEffect } from 'react';
import { Form, Input, Button, InputNumber, Spin, message, Modal } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useCampaignForm } from '../hooks/useCampaignForm';
import { GlobalVendorDropdown } from 'features/vendors';
import { toast } from 'react-toastify';

interface CampaignFormProps {
  onSuccess?: () => void;
  visible?: boolean;
  onCancel?: () => void;
}

/**
 * Campaign Form Component
 * Batch form for adding/creating campaigns
 */
export const CampaignForm: React.FC<CampaignFormProps> = ({ onSuccess, visible = false, onCancel }) => {
  const selectedOfficeId = useSelector((state: any) => state.dropDown?.officeId);
  const {
    campaigns,
    loading,
    error,
    success,
    addCampaign,
    removeCampaign,
    updateCampaign,
    submitCampaigns,
    resetForm,
    clearMessage,
  } = useCampaignForm();

  const [form] = Form.useForm();

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearMessage();
    }
  }, [error, clearMessage]);

  useEffect(() => {
    if (success) {
      toast.success('Campaigns added successfully!');
      form.resetFields();
      resetForm();
      onSuccess?.();
      clearMessage();
    }
  }, [success, onSuccess, clearMessage, form, resetForm]);

  // Update campaigns with officeId whenever selectedOfficeId changes
  useEffect(() => {
    if (selectedOfficeId && campaigns.length > 0) {
      campaigns.forEach((campaign, index) => {
        if (campaign.officeId !== selectedOfficeId) {
          updateCampaign(index, {
            ...campaign,
            officeId: selectedOfficeId,
          });
        }
      });
    }
  }, [selectedOfficeId, campaigns, updateCampaign]);

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const result = await submitCampaigns();
      if (result) {
        message.success('Campaigns created successfully');
        onSuccess?.();
        onCancel?.(); // Close the modal which unmounts the component
      }
    } catch (validationError) {
      // Form validation failed
    }
  };

  return (
    <Modal
      title='Add Campaigns'
      open={visible}
      onCancel={onCancel}
      destroyOnClose
      width={1400}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      footer={[
        <Button key='cancel' onClick={onCancel}>
          Cancel
        </Button>,
        <Button key='submit' type='primary' onClick={handleSubmit} loading={loading}>
          Submit Campaigns
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Form
            form={form}
            layout='vertical'
            onValuesChange={(changedValues, allValues) => {
              // Track form changes and ensure all required fields are set
              campaigns.forEach((_, index) => {
                const formData = allValues[`campaign_${index}`];
                if (formData) {
                  // Get the vendor object if globalId was selected via dropdown
                  const currentCampaign = campaigns[index];
                  updateCampaign(index, {
                    queueId: formData.queueId || 0,
                    vendorName: formData.vendorName || '',
                    globalName: currentCampaign.globalName || formData.globalName || '',
                    globalId: formData.globalId || '',
                    cost: formData.cost,
                    buffer: formData.buffer,
                    officeId: selectedOfficeId || '',
                  });
                }
              });
            }}
          >
            {campaigns.map((campaign, index) => (
              <Form.Item key={`campaign_group_${index}`} noStyle>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr auto',
                    gap: '16px',
                    padding: '12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    marginBottom: '12px',
                    alignItems: 'end',
                  }}
                >
                  <Form.Item
                    name={[`campaign_${index}`, 'queueId']}
                    label='Queue ID'
                    rules={[{ required: true, message: 'Queue ID is required' }]}
                    initialValue={campaign.queueId}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber placeholder='e.g., 1' min={1} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name={[`campaign_${index}`, 'vendorName']}
                    label='Vendor Name'
                    rules={[{ required: true, message: 'Vendor Name is required' }]}
                    initialValue={campaign.vendorName}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder='e.g., Acme Sales' style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name={[`campaign_${index}`, 'globalId']}
                    label='Global Name'
                    initialValue={campaign.globalId}
                    style={{ marginBottom: 0 }}
                  >
                    <GlobalVendorDropdown
                      value={campaign.globalId}
                      onChange={(globalId, vendor) => {
                        updateCampaign(index, {
                          globalId,
                          globalName: vendor?.globalName || '',
                        });
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    name={[`campaign_${index}`, 'cost']}
                    label='Cost'
                    initialValue={campaign.cost}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber placeholder='0' min={0} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name={[`campaign_${index}`, 'buffer']}
                    label='Buffer %'
                    initialValue={campaign.buffer}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber placeholder='0' min={0} max={100} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item label=' ' style={{ marginBottom: 0 }}>
                    <Button
                      type='dashed'
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeCampaign(index)}
                      disabled={campaigns.length === 1}
                    >
                      Remove
                    </Button>
                  </Form.Item>
                </div>
              </Form.Item>
            ))}
          </Form>

          <Button
            type='dashed'
            icon={<PlusOutlined />}
            onClick={addCampaign}
            block
          >
            Add Campaign
          </Button>
        </div>
      </Spin>
    </Modal>
  );
};

export default CampaignForm;
